import { forwardRef } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success' | 'link';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'icon' | 'icon-sm';

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-accent text-fg-onAccent shadow-xs hover:bg-accent-hover active:bg-accent-hover disabled:bg-accent/50',
  secondary:
    'bg-surface text-fg border border-subtle shadow-xs hover:bg-surface-hover active:bg-surface-sunken',
  ghost: 'text-muted hover:bg-surface-hover hover:text-fg',
  danger: 'bg-danger text-white shadow-xs hover:brightness-110 active:brightness-95',
  success: 'bg-success text-white shadow-xs hover:brightness-110 active:brightness-95',
  link: 'text-accent-fg underline-offset-4 hover:underline p-0 h-auto',
};

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-7 px-2 text-xs gap-1 rounded',
  sm: 'h-8 px-2.5 text-sm gap-1.5 rounded-md',
  md: 'h-9 px-3.5 text-sm gap-2 rounded-md',
  lg: 'h-11 px-5 text-base gap-2 rounded-lg',
  icon: 'h-9 w-9 rounded-md',
  'icon-sm': 'h-8 w-8 rounded-md',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Shows a spinner and blocks interaction. Keeps its width so nothing shifts. */
  loading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    className,
    variant = 'secondary',
    size = 'md',
    loading = false,
    leftIcon,
    rightIcon,
    fullWidth,
    disabled,
    children,
    type = 'button',
    ...props
  },
  ref,
) {
  const isIconOnly = size === 'icon' || size === 'icon-sm';
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        'relative inline-flex select-none items-center justify-center whitespace-nowrap font-medium',
        'transition-colors duration-100',
        'disabled:pointer-events-none disabled:opacity-60',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? (
        <Spinner size={size === 'lg' ? 'md' : 'sm'} label={null} className="absolute" />
      ) : null}
      {/* `contents` keeps the button's own flex gap in play; `invisible` inherits
          down to the children so the button never changes width while loading. */}
      <span className={cn('contents', loading && 'invisible')}>
        {leftIcon}
        {isIconOnly ? children : <span className="truncate">{children}</span>}
        {rightIcon}
      </span>
    </button>
  );
});

/**
 * A router link that looks like a Button. Use this rather than nesting a <Link>
 * inside a <Button> — that produces invalid, unfocusable markup.
 */
export interface LinkButtonProps extends LinkProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
}

export function LinkButton({
  variant = 'secondary',
  size = 'md',
  leftIcon,
  rightIcon,
  fullWidth,
  className,
  children,
  ...props
}: LinkButtonProps) {
  return (
    <Link
      className={cn(
        'inline-flex select-none items-center justify-center whitespace-nowrap font-medium transition-colors duration-100',
        VARIANTS[variant],
        SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {leftIcon}
      {children}
      {rightIcon}
    </Link>
  );
}

/** A button rendered inside a toolbar group — squares off adjoining corners. */
export function ButtonGroup({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div
      role="group"
      className={cn(
        'inline-flex items-center [&>*:not(:first-child)]:rounded-l-none [&>*:not(:last-child)]:rounded-r-none [&>*:not(:last-child)]:border-r-0',
        className,
      )}
    >
      {children}
    </div>
  );
}
